import { apiClient } from '@/lib/api-client';
import { saveOrShareBlob } from '@/lib/nativeFile';
import {
  Cronograma,
  CronogramaSecao,
  CronogramaAtividade,
  CronogramaAtividadeAnexo,
  CronogramaAtividadeStatus,
  CronogramaAtividadeSubtarefa,
  CronogramaResponsavel,
  CronogramaDashboard,
  CronogramaHistorico,
  OKR,
  KeyResult,
  ApiResponse,
} from '@/types';

export const cronogramaService = {
  async getAll(empresa_id?: string): Promise<ApiResponse<Cronograma[]>> {
    try {
      const params = empresa_id ? `?empresa_id=${empresa_id}` : '';
      return await apiClient.get<ApiResponse<Cronograma[]>>(`/cronogramas${params}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar cronogramas', data: [] };
    }
  },

  async getById(id: string): Promise<ApiResponse<Cronograma>> {
    try {
      return await apiClient.get<ApiResponse<Cronograma>>(`/cronogramas/${id}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar cronograma', data: {} as Cronograma };
    }
  },

  async create(data: Partial<Cronograma>): Promise<ApiResponse<Cronograma>> {
    try {
      return await apiClient.post<ApiResponse<Cronograma>>('/cronogramas', data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao criar cronograma', data: {} as Cronograma };
    }
  },

  async update(id: string, data: Partial<Cronograma>): Promise<ApiResponse<Cronograma>> {
    try {
      return await apiClient.put<ApiResponse<Cronograma>>(`/cronogramas/${id}`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao atualizar cronograma', data: {} as Cronograma };
    }
  },

  async delete(id: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(`/cronogramas/${id}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao excluir cronograma', data: false };
    }
  },

  async getDashboard(id: string): Promise<ApiResponse<CronogramaDashboard>> {
    try {
      return await apiClient.get<ApiResponse<CronogramaDashboard>>(`/cronogramas/${id}/dashboard`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar dashboard', data: {} as CronogramaDashboard };
    }
  },

  async getHistorico(id: string): Promise<ApiResponse<CronogramaHistorico[]>> {
    try {
      return await apiClient.get<ApiResponse<CronogramaHistorico[]>>(`/cronogramas/${id}/historico`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar histórico', data: [] };
    }
  },

  async criarAPartir(modeloId: string, data: { titulo: string; area: string; empresa_id?: string }): Promise<ApiResponse<Cronograma>> {
    try {
      return await apiClient.post<ApiResponse<Cronograma>>(`/cronogramas/${modeloId}/criar-a-partir`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao criar cronograma', data: {} as Cronograma };
    }
  },

  getExportUrl(id: string): string {
    const base = (window as any).__ENV__?.VITE_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    return `${base}/cronogramas/${id}/export`;
  },

  async exportExcel(id: string): Promise<void> {
    const blob = await apiClient.getBlob(`/cronogramas/${id}/export`);
    await saveOrShareBlob(blob, `cronograma_${id}.xlsx`);
  },

  async compartilhar(id: string): Promise<ApiResponse<{ share_token: string }>> {
    try {
      return await apiClient.post<ApiResponse<{ share_token: string }>>(`/cronogramas/${id}/compartilhar`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao gerar link', data: { share_token: '' } };
    }
  },

  async revogarCompartilhamento(id: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(`/cronogramas/${id}/compartilhar`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao revogar link', data: false };
    }
  },
};

export async function getCronogramaPublico(token: string): Promise<ApiResponse<Cronograma & { progresso: number; total_atividades: number; dashboard: { total: number; porStatus: Array<{ status: string; count: number; percentual: number }> } }>> {
  try {
    const base = (window as any).__ENV__?.VITE_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
    const res = await fetch(`${base}/public/cronogramas/${token}`);
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  } catch (error) {
    return { success: false, error: error instanceof Error ? error.message : 'Erro ao carregar', data: {} as any };
  }
}

export const cronogramaSecaoService = {
  async create(cronogramaId: string, data: Partial<CronogramaSecao>): Promise<ApiResponse<CronogramaSecao>> {
    try {
      return await apiClient.post<ApiResponse<CronogramaSecao>>(`/cronogramas/${cronogramaId}/secoes`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao criar seção', data: {} as CronogramaSecao };
    }
  },

  async update(cronogramaId: string, secaoId: string, data: Partial<CronogramaSecao>): Promise<ApiResponse<CronogramaSecao>> {
    try {
      return await apiClient.put<ApiResponse<CronogramaSecao>>(`/cronogramas/${cronogramaId}/secoes/${secaoId}`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao atualizar seção', data: {} as CronogramaSecao };
    }
  },

  async delete(cronogramaId: string, secaoId: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(`/cronogramas/${cronogramaId}/secoes/${secaoId}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao excluir seção', data: false };
    }
  },
};

export const cronogramaAtividadeService = {
  async create(cronogramaId: string, data: Partial<CronogramaAtividade>): Promise<ApiResponse<CronogramaAtividade>> {
    try {
      return await apiClient.post<ApiResponse<CronogramaAtividade>>(`/cronogramas/${cronogramaId}/atividades`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao criar atividade', data: {} as CronogramaAtividade };
    }
  },

  async update(cronogramaId: string, atividadeId: string, data: Partial<CronogramaAtividade>): Promise<ApiResponse<CronogramaAtividade>> {
    try {
      return await apiClient.put<ApiResponse<CronogramaAtividade>>(`/cronogramas/${cronogramaId}/atividades/${atividadeId}`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao atualizar atividade', data: {} as CronogramaAtividade };
    }
  },

  async updateStatus(cronogramaId: string, atividadeId: string, status: CronogramaAtividadeStatus): Promise<ApiResponse<CronogramaAtividade>> {
    try {
      return await apiClient.patch<ApiResponse<CronogramaAtividade>>(`/cronogramas/${cronogramaId}/atividades/${atividadeId}/status`, { status });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao atualizar status', data: {} as CronogramaAtividade };
    }
  },

  async delete(cronogramaId: string, atividadeId: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(`/cronogramas/${cronogramaId}/atividades/${atividadeId}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao excluir atividade', data: false };
    }
  },

  async moveAtividade(cronogramaId: string, atividadeId: string, secaoId: string): Promise<ApiResponse<CronogramaAtividade>> {
    try {
      return await apiClient.patch<ApiResponse<CronogramaAtividade>>(
        `/cronogramas/${cronogramaId}/atividades/${atividadeId}/mover`,
        { secao_id: secaoId }
      );
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao mover atividade', data: undefined as any };
    }
  },

  async reordenar(cronogramaId: string, secaoId: string, atividadeIds: string[]): Promise<ApiResponse<CronogramaAtividade[]>> {
    try {
      return await apiClient.patch<ApiResponse<CronogramaAtividade[]>>(
        `/cronogramas/${cronogramaId}/secoes/${secaoId}/atividades/reordenar`,
        { atividade_ids: atividadeIds }
      );
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao reordenar atividades', data: [] };
    }
  },

  // A seção vira o OKR (criado se ainda não existir) e a atividade vira um Key Result dele.
  async converter(
    cronogramaId: string,
    atividadeId: string,
  ): Promise<ApiResponse<{ okr: OKR; okrCriado: boolean; keyResult: KeyResult }>> {
    try {
      return await apiClient.post<ApiResponse<{ okr: OKR; okrCriado: boolean; keyResult: KeyResult }>>(
        `/cronogramas/${cronogramaId}/atividades/${atividadeId}/converter`,
        {}
      );
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao converter atividade', data: {} as any };
    }
  },
};

export const cronogramaSubtarefaService = {
  async create(cronogramaId: string, atividadeId: string, titulo: string): Promise<ApiResponse<CronogramaAtividadeSubtarefa>> {
    try {
      return await apiClient.post<ApiResponse<CronogramaAtividadeSubtarefa>>(
        `/cronogramas/${cronogramaId}/atividades/${atividadeId}/subtarefas`,
        { titulo }
      );
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao criar subtarefa', data: {} as CronogramaAtividadeSubtarefa };
    }
  },

  async update(
    cronogramaId: string, atividadeId: string, subtarefaId: string,
    data: Partial<Pick<CronogramaAtividadeSubtarefa, 'titulo' | 'concluida'>>
  ): Promise<ApiResponse<CronogramaAtividadeSubtarefa>> {
    try {
      return await apiClient.put<ApiResponse<CronogramaAtividadeSubtarefa>>(
        `/cronogramas/${cronogramaId}/atividades/${atividadeId}/subtarefas/${subtarefaId}`,
        data
      );
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao atualizar subtarefa', data: {} as CronogramaAtividadeSubtarefa };
    }
  },

  async delete(cronogramaId: string, atividadeId: string, subtarefaId: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(
        `/cronogramas/${cronogramaId}/atividades/${atividadeId}/subtarefas/${subtarefaId}`
      );
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao excluir subtarefa', data: false };
    }
  },
};

export const cronogramaAnexoService = {
  async list(cronogramaId: string, atividadeId: string): Promise<ApiResponse<CronogramaAtividadeAnexo[]>> {
    try {
      return await apiClient.get<ApiResponse<CronogramaAtividadeAnexo[]>>(
        `/cronogramas/${cronogramaId}/atividades/${atividadeId}/anexos`
      );
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar anexos', data: [] };
    }
  },

  async upload(cronogramaId: string, atividadeId: string, file: File): Promise<ApiResponse<CronogramaAtividadeAnexo>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      return await apiClient.postMultipart<ApiResponse<CronogramaAtividadeAnexo>>(
        `/cronogramas/${cronogramaId}/atividades/${atividadeId}/anexos`,
        formData
      );
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao enviar anexo', data: {} as CronogramaAtividadeAnexo };
    }
  },

  async getConteudoBlob(cronogramaId: string, atividadeId: string, anexoId: string): Promise<Blob> {
    return apiClient.getBlob(
      `/cronogramas/${cronogramaId}/atividades/${atividadeId}/anexos/${anexoId}/conteudo`
    );
  },

  async delete(cronogramaId: string, atividadeId: string, anexoId: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(
        `/cronogramas/${cronogramaId}/atividades/${atividadeId}/anexos/${anexoId}`
      );
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao excluir anexo', data: false };
    }
  },
};

export const cronogramaResponsavelService = {
  async getAll(cronogramaId: string): Promise<ApiResponse<CronogramaResponsavel[]>> {
    try {
      return await apiClient.get<ApiResponse<CronogramaResponsavel[]>>(`/cronogramas/${cronogramaId}/responsaveis`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar responsáveis', data: [] };
    }
  },

  async create(cronogramaId: string, data: Partial<CronogramaResponsavel>): Promise<ApiResponse<CronogramaResponsavel>> {
    try {
      return await apiClient.post<ApiResponse<CronogramaResponsavel>>(`/cronogramas/${cronogramaId}/responsaveis`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao criar responsável', data: {} as CronogramaResponsavel };
    }
  },

  async update(cronogramaId: string, responsavelId: string, data: Partial<CronogramaResponsavel>): Promise<ApiResponse<CronogramaResponsavel>> {
    try {
      return await apiClient.put<ApiResponse<CronogramaResponsavel>>(`/cronogramas/${cronogramaId}/responsaveis/${responsavelId}`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao atualizar responsável', data: {} as CronogramaResponsavel };
    }
  },

  async delete(cronogramaId: string, responsavelId: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(`/cronogramas/${cronogramaId}/responsaveis/${responsavelId}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao excluir responsável', data: false };
    }
  },
};
